import { createContext, useContext, useEffect, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Abc3Context } from '../../context/Abc3Context';
import { CognitoAuthContext } from '../../context/CognitoAuthContext';

export const TradesContext = createContext();
export const useTrades = () => useContext(TradesContext);

export const TradesProvider = ({ children }) => {
  const { user } = useContext(Abc3Context);
  const { userGroups } = useContext(CognitoAuthContext);
  const [trades, setTrades] = useState({});
  const [partyLookup, setPartyLookup] = useState({});

  const tradesApiUrl = process.env.REACT_APP_TRADES_API;

  const getRole = () => {
    if (userGroups?.includes('admin')) return 'admin';
    if (userGroups?.includes('manager')) return 'manager';
    if (userGroups?.includes('backoffice')) return 'backoffice';
    if (userGroups?.includes('trader') || userGroups?.includes('user')) return 'trader';
    return 'trader';
  };

  const fetchPartiesFromApi = async () => {
    if (!tradesApiUrl) return;
    try {
      const response = await fetch(`${tradesApiUrl}/parties`);
      if (!response.ok) return;
      const data = await response.json();
      const lookup = data.items?.reduce((acc, party) => {
        acc[party.partyId] = {
          organization: party.organization || party.name || party.partyId
        };
        return acc;
      }, {}) || {};
      setPartyLookup(lookup);
    } catch (error) {
      console.error('Error fetching parties:', error);
    }
  };

  const getPartyOrganization = (partyId) => {
    if (!partyId) return '';
    return partyLookup[partyId]?.organization || partyId;
  };

  const filterTradesByOrganization = (items) => {
    if (!user?.party) return [];
    const userOrg = getPartyOrganization(user.party);
    if (!userOrg) return [];
    return items.filter((trade) => {
      const entityOrg = getPartyOrganization(trade.entity);
      const cptyOrg = getPartyOrganization(trade.cpty);
      return entityOrg === userOrg || cptyOrg === userOrg;
    });
  };

  const fetchTradesFromApi = async () => {
    if (!tradesApiUrl || !user?.party) return;

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      const role = getRole();
      const isOrgScope = role === 'manager' || role === 'backoffice';
      const isAdmin = role === 'admin';
      const endpoint = isAdmin || isOrgScope
        ? `${tradesApiUrl}/admin/trades`
        : `${tradesApiUrl}/trades?party=${encodeURIComponent(user.party)}`;

      const response = await fetch(endpoint, {
        headers: {
          Authorization: token ? `Bearer ${token}` : ''
        }
      });

      if (!response.ok) {
        console.error(`Failed to load trades: ${response.status}`);
        return;
      }

      const data = await response.json();
      let items = data.items || [];
      if (isOrgScope) {
        items = filterTradesByOrganization(items);
      }
      const mapped = items.reduce((acc, trade) => {
        const tradeId = trade.tradeId || trade.id || trade.TRADE_ID;
        if (tradeId) {
          acc[tradeId] = trade;
        }
        return acc;
      }, {});

      setTrades(mapped);
    } catch (error) {
      console.error('Error fetching trades:', error);
    }
  };

  useEffect(() => {
    if (!user?.party) return;
    fetchPartiesFromApi();
  }, [user?.party, tradesApiUrl]);

  useEffect(() => {
    if (!user?.party) return;

    fetchTradesFromApi();
    const interval = setInterval(() => {
      if (typeof window !== 'undefined' && window.__gridInteracting) {
        return;
      }
      fetchTradesFromApi();
    }, 5000);
    return () => clearInterval(interval);
  }, [user?.party, tradesApiUrl, userGroups, partyLookup]);

  return (
    <TradesContext.Provider value={trades}>
      {children}
    </TradesContext.Provider>
  );
};
